import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import { Upload, Plus, Trash2, ChevronDown, ChevronRight, Download, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';

type FieldType = 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date';

interface SchemaField {
  name: string;
  type: FieldType;
  value?: any;
  children?: SchemaField[];
  isCustom?: boolean;
  isExpanded?: boolean;
}

type ViewMode = 'json' | 'fields' | 'view';

const DataManagementPreferencesPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('json');
  const [jsonInput, setJsonInput] = useState('');
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const [jsonError, setJsonError] = useState<string>('');
  const [savedSchema, setSavedSchema] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      setLoadError('');
      const response = await api.get<any>('/api/preferences');
      
      if (response && response.dataManagement) {
        const schema = response.dataManagement;
        setSavedSchema(schema);
        const fields = parseJsonToFields(schema);
        setSchemaFields(fields);
        setViewMode('view');
      }
    } catch (error: any) {
      if (error?.code !== 'PREF0001') {
        console.error('Error loading preferences:', error);
        setLoadError(error?.userMessage || 'Failed to load preferences');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const parseJsonToFields = (obj: any, parentPath: string = ''): SchemaField[] => {
    const fields: SchemaField[] = [];
    
    for (const [key, value] of Object.entries(obj)) {
      const field: SchemaField = {
        name: key,
        type: Array.isArray(value) ? 'array' : typeof value as FieldType,
        value: undefined,
        isExpanded: true,
      };

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        field.type = 'object';
        field.children = parseJsonToFields(value, `${parentPath}${key}.`);
      } else if (Array.isArray(value)) {
        field.type = 'array';
        if (value.length > 0) {
          if (typeof value[0] === 'object' && value[0] !== null) {
            field.children = [];
            value.forEach((item, index) => {
              const itemFields = parseJsonToFields(item, `${parentPath}${key}[${index}].`);
              field.children!.push(...itemFields.map(f => ({
                ...f,
                name: `[${index}].${f.name}`,
                isExpanded: true,
              })));
            });
            field.value = undefined;
          } else {
            field.value = value;
          }
        } else {
          field.value = [];
        }
      } else {
        if (key === 'categoryType' || key === 'type' || key === 'kind') {
          field.value = value;
        }
      }

      fields.push(field);
    }

    return fields;
  };

  const handleJsonSubmit = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      const fields = parseJsonToFields(parsed);
      setSchemaFields(fields);
      setJsonError('');
      setViewMode('fields');
    } catch (error) {
      setJsonError('Invalid JSON format. Please check your input.');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setJsonInput(content);
        try {
          const parsed = JSON.parse(content);
          const fields = parseJsonToFields(parsed);
          setSchemaFields(fields);
          setJsonError('');
          setViewMode('fields');
        } catch (error) {
          setJsonError('Invalid JSON file. Please check the file content.');
        }
      };
      reader.readAsText(file);
    }
  };

  const fieldsToJson = (fields: SchemaField[]): any => {
    const result: any = {};
    
    fields.forEach(field => {
      const arrayItemMatch = field.name.match(/^\[(\d+)\]\.(.+)$/);
      
      if (arrayItemMatch) {
        return;
      }
      
      if (field.type === 'object' && field.children) {
        result[field.name] = fieldsToJson(field.children);
      } else if (field.type === 'array') {
        if (field.children && field.children.length > 0) {
          const arrayItems: Map<number, SchemaField[]> = new Map();
          
          field.children.forEach(child => {
            const match = child.name.match(/^\[(\d+)\]\.(.+)$/);
            if (match) {
              const index = parseInt(match[1]);
              const fieldName = match[2];
              
              if (!arrayItems.has(index)) {
                arrayItems.set(index, []);
              }
              
              arrayItems.get(index)!.push({
                ...child,
                name: fieldName,
              });
            }
          });

          if (arrayItems.size > 0) {
            result[field.name] = Array.from(arrayItems.entries())
              .sort(([a], [b]) => a - b)
              .map(([_, itemFields]) => fieldsToJson(itemFields));
          } else {
            result[field.name] = [fieldsToJson(field.children)];
          }
        } else {
          result[field.name] = field.value ?? [];
        }
      } else {
        result[field.name] = field.value ?? getDefaultValue(field.type);
      }
    });

    return result;
  };

  const getDefaultValue = (type: FieldType): any => {
    switch (type) {
      case 'string': return '';
      case 'number': return 0;
      case 'boolean': return false;
      case 'array': return [];
      case 'object': return {};
      case 'date': return new Date().toISOString();
      default: return '';
    }
  };

  const addCustomField = (path: number[], parentFields: SchemaField[] = schemaFields) => {
    const newField: SchemaField = {
      name: 'newField',
      type: 'string',
      value: '',
      isCustom: true,
      isExpanded: true,
    };

    if (path.length === 0) {
      setSchemaFields([...parentFields, newField]);
    } else {
      const updatedFields = [...parentFields];
      let current = updatedFields;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (current[path[i]].children) {
          current = current[path[i]].children!;
        }
      }
      
      const lastIndex = path[path.length - 1];
      if (!current[lastIndex].children) {
        current[lastIndex].children = [];
      }
      current[lastIndex].children!.push(newField);
      
      setSchemaFields(updatedFields);
    }
  };

  const updateField = (path: number[], updates: Partial<SchemaField>) => {
    const updatedFields = [...schemaFields];
    let current = updatedFields;
    
    for (let i = 0; i < path.length - 1; i++) {
      if (current[path[i]].children) {
        current = current[path[i]].children!;
      }
    }
    
    const lastIndex = path[path.length - 1];
    current[lastIndex] = { ...current[lastIndex], ...updates };
    
    if (updates.type === 'object' || updates.type === 'array') {
      if (!current[lastIndex].children) {
        current[lastIndex].children = [];
      }
    } else {
      delete current[lastIndex].children;
    }
    
    setSchemaFields(updatedFields);
  };

  const deleteField = (path: number[]) => {
    const updatedFields = [...schemaFields];
    
    if (path.length === 1) {
      updatedFields.splice(path[0], 1);
    } else {
      let current = updatedFields;
      
      for (let i = 0; i < path.length - 1; i++) {
        if (current[path[i]].children) {
          current = current[path[i]].children!;
        }
      }
      
      current.splice(path[path.length - 1], 1);
    }
    
    setSchemaFields(updatedFields);
  };

  const toggleExpand = (path: number[]) => {
    const updatedFields = [...schemaFields];
    let current = updatedFields;
    
    for (let i = 0; i < path.length - 1; i++) {
      if (current[path[i]].children) {
        current = current[path[i]].children!;
      }
    }
    
    const lastIndex = path[path.length - 1];
    current[lastIndex].isExpanded = !current[lastIndex].isExpanded;
    
    setSchemaFields(updatedFields);
  };

  const handleSaveSchema = async () => {
    try {
      setIsLoading(true);
      
      const schemaJson = fieldsToJson(schemaFields);
      const documentData = schemaJson.dataManagement?.document || schemaJson;
      
      const payload = {
        dataManagement: {
          document: {
            categoryTypes: documentData.categoryTypes || [],
            categories: (documentData.categories || []).map((cat: any) => {
              if (cat.records && Array.isArray(cat.records) && cat.records.length > 0) {
                const firstRecord = cat.records[0];

                const indexedProperties = Object.keys(firstRecord).map(key => ({
                  name: key,
                  type: typeof firstRecord[key] === 'number' ? 'number' : 
                        typeof firstRecord[key] === 'boolean' ? 'boolean' :
                        Array.isArray(firstRecord[key]) ? 'array' :
                        typeof firstRecord[key] === 'object' && firstRecord[key] !== null ? 'object' : 'string',
                  required: false,
                  description: '',
                  properties: [],
                  items: undefined
                }));
                
                return {
                  categoryType: cat.categoryType || 'default',
                  indexedProperties: indexedProperties,
                  metadata: []
                };
              }

              return {
                categoryType: cat.categoryType || 'default',
                indexedProperties: (cat.indexedProperties || []).map((prop: any) => ({
                  name: prop.name || '',
                  type: prop.type || 'string',
                  required: prop.required || false,
                  description: prop.description || '',
                  properties: prop.properties || [],
                  items: prop.items
                })),
                metadata: (cat.metadata || []).map((prop: any) => ({
                  name: prop.name || '',
                  type: prop.type || 'string',
                  required: prop.required || false,
                  description: prop.description || '',
                  properties: prop.properties || [],
                  items: prop.items
                }))
              };
            })
          }
        }
      };
      
      const response = await api.put<any>('/api/preferences', payload);

      setSavedSchema(response.dataManagement);
      setViewMode('view');
      alert('Schema saved successfully!');
    } catch (error: any) {
      console.error('Error saving schema:', error);
      alert(error?.userMessage || 'Failed to save schema. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevertToDefault = () => {
    setViewMode('json');
    setJsonInput('');
    setSchemaFields([]);
  };

  const handleAbort = () => {
    setViewMode('json');
  };

  const handleDeleteSchema = () => {
    if (confirm('Are you sure you want to delete the schema? This action cannot be undone.')) {
      deleteSchema();
    }
  };

  const deleteSchema = async () => {
    try {
      setIsLoading(true);
      await api.put('/api/preferences', {
        dataManagement: {
          document: {
            categoryTypes: [],
            categories: []
          }
        }
      });
      
      setJsonInput('');
      setSchemaFields([]);
      setSavedSchema(null);
      setViewMode('json');
      console.log('Schema deleted successfully');
    } catch (error: any) {
      console.error('Error deleting schema:', error);
      alert(error?.userMessage || 'Failed to delete schema. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadSchema = () => {
    const schemaJson = savedSchema || fieldsToJson(schemaFields);
    const dataStr = JSON.stringify(schemaJson, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `schema-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderReadOnlyField = (field: SchemaField, path: number[], depth: number = 0) => {
    const hasChildren = field.children && field.children.length > 0;
    const canHaveChildren = field.type === 'object' || field.type === 'array';
    const isArrayOfPrimitives = field.type === 'array' && !hasChildren;

    return (
      <div key={path.join('-')} className="space-y-2" style={{ marginLeft: `${depth * 24}px` }}>
        <div className="flex items-center space-x-2">
          {canHaveChildren && (
            <button
              onClick={() => toggleExpand(path)}
              className="p-1 dark:text-text-secondary text-light-text-secondary hover:text-primary"
            >
              {field.isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          
          {!canHaveChildren && <div className="w-6"></div>}
          
          <span className="px-2 py-1 text-sm dark:text-text-primary text-light-text-primary font-semibold w-40">
            {field.name}
          </span>

          <span className="px-2 py-1 text-xs dark:bg-gray-700 bg-gray-200 dark:text-text-secondary text-light-text-secondary rounded">
            {field.type}
          </span>

          {!canHaveChildren && (
            <span className="flex-1 px-2 py-1 text-sm dark:text-text-secondary text-light-text-secondary">
              {field.type === 'boolean' ? (field.value ? '✓ true' : '✗ false') : (field.value ?? 'N/A')}
            </span>
          )}

          {isArrayOfPrimitives && (
            <span className="flex-1 px-2 py-1 text-sm dark:text-text-secondary text-light-text-secondary font-mono">
              {Array.isArray(field.value) ? JSON.stringify(field.value) : '[]'}
            </span>
          )}
        </div>

        {canHaveChildren && field.isExpanded && hasChildren && (
          <div className="space-y-2">
            {field.children?.map((child, index) => renderReadOnlyField(child, [...path, index], depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderField = (field: SchemaField, path: number[], depth: number = 0) => {
    const hasChildren = field.children && field.children.length > 0;
    const canHaveChildren = field.type === 'object' || field.type === 'array';
    const isArrayOfPrimitives = field.type === 'array' && !hasChildren;

    return (
      <div key={path.join('-')} className="space-y-2" style={{ marginLeft: `${depth * 24}px` }}>
        <div className="flex items-center space-x-2 group">
          {canHaveChildren && (
            <button
              onClick={() => toggleExpand(path)}
              className="p-1 dark:text-text-secondary text-light-text-secondary hover:text-primary"
            >
              {field.isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
          
          {!canHaveChildren && <div className="w-6"></div>}
          
          <input
            type="text"
            value={field.name}
            onChange={(e) => updateField(path, { name: e.target.value })}
            className="px-2 py-1 text-sm dark:bg-background bg-light-background border dark:border-border-color border-light-border-color rounded dark:text-text-primary text-light-text-primary w-40"
            placeholder="Field name"
          />

          <select
            value={field.type}
            onChange={(e) => updateField(path, { type: e.target.value as FieldType })}
            className="px-2 py-1 text-sm dark:bg-background bg-light-background border dark:border-border-color border-light-border-color rounded dark:text-text-primary text-light-text-primary"
          >
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="array">Array</option>
            <option value="object">Object</option>
            <option value="date">Date</option>
          </select>

          {!canHaveChildren && (
            <input
              type={field.type === 'number' ? 'number' : field.type === 'boolean' ? 'checkbox' : 'text'}
              checked={field.type === 'boolean' ? field.value : undefined}
              value={field.type === 'boolean' ? undefined : (field.value ?? '')}
              onChange={(e) => {
                const newValue = field.type === 'number' 
                  ? Number(e.target.value) 
                  : field.type === 'boolean'
                  ? e.target.checked
                  : e.target.value;
                updateField(path, { value: newValue });
              }}
              className="flex-1 px-2 py-1 text-sm dark:bg-background bg-light-background border dark:border-border-color border-light-border-color rounded dark:text-text-primary text-light-text-primary"
              placeholder="Default value"
            />
          )}

          {isArrayOfPrimitives && (
            <input
              type="text"
              value={Array.isArray(field.value) ? JSON.stringify(field.value) : '[]'}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  if (Array.isArray(parsed)) {
                    updateField(path, { value: parsed });
                  }
                } catch (err) {
                  // Invalid JSON, ignore
                }
              }}
              className="flex-1 px-2 py-1 text-sm dark:bg-background bg-light-background border dark:border-border-color border-light-border-color rounded dark:text-text-primary text-light-text-primary font-mono"
              placeholder='["value1", "value2"]'
            />
          )}

          {canHaveChildren && (
            <button
              onClick={() => addCustomField([...path])}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-primary hover:text-primary-hover border border-primary rounded hover:bg-primary hover:bg-opacity-10 transition-colors"
            >
              <Plus className="w-3 h-3" />
              <span>Add field</span>
            </button>
          )}

          {field.isCustom && (
            <button
              onClick={() => deleteField(path)}
              className="p-1 text-red-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {canHaveChildren && field.isExpanded && hasChildren && (
          <div className="space-y-2">
            {field.children?.map((child, index) => renderField(child, [...path, index], depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold dark:text-text-primary text-light-text-primary mb-2">
          Data Management Preferences
        </h1>
        <p className="text-sm md:text-base dark:text-text-secondary text-light-text-secondary">
          Configure your data management and storage preferences
        </p>
      </div>

      <Card className="p-4 md:p-6">
        <h2 className="text-xl font-semibold dark:text-text-primary text-light-text-primary mb-4">
          Default Data Structure Schema
        </h2>
        
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-sm dark:text-text-secondary text-light-text-secondary">Loading...</p>
          </div>
        )}

        {loadError && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 rounded-md">
            <p className="text-sm text-red-800 dark:text-red-200">{loadError}</p>
          </div>
        )}
        
        {!isLoading && viewMode === 'json' ? (
          <div className="space-y-4">
            <p className="text-sm dark:text-text-secondary text-light-text-secondary">
              Define your default data structure by uploading a JSON file, pasting JSON, or creating from scratch.
            </p>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <label className="flex items-center justify-center space-x-2 px-4 py-2 border dark:border-border-color border-light-border-color rounded-md dark:text-text-primary text-light-text-primary hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                <span>Upload JSON File</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              
              <div className="flex-1 border-t dark:border-border-color border-light-border-color"></div>
              <span className="dark:text-text-secondary text-light-text-secondary text-sm">or</span>
              <div className="flex-1 border-t dark:border-border-color border-light-border-color"></div>

              <button
                onClick={() => {
                  setSchemaFields([]);
                  setViewMode('fields');
                }}
                className="flex items-center justify-center space-x-2 px-4 py-2 border dark:border-border-color border-light-border-color rounded-md dark:text-text-primary text-light-text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Create from Scratch</span>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium dark:text-text-primary text-light-text-primary mb-2">
                Paste JSON Schema
              </label>
              <div className="mb-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                <p className="text-blue-800 dark:text-blue-200 font-medium mb-1">Expected format:</p>
                <pre className="text-xs text-blue-700 dark:text-blue-300 overflow-x-auto">
{`{
  "categoryTypes": ["restaurant", "bar"],
  "categories": [{
    "categoryType": "restaurant",
    "indexedProperties": [{
      "name": "cuisine",
      "type": "string",
      "required": true
    }],
    "metadata": [{
      "name": "rating",
      "type": "number"
    }]
  }]
}`}
                </pre>
              </div>
              <textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                className="w-full h-64 px-3 py-2 dark:bg-background bg-light-background border dark:border-border-color border-light-border-color rounded-md dark:text-text-primary text-light-text-primary font-mono text-sm"
                placeholder='{\n  "categoryTypes": ["type1", "type2"],\n  "categories": [\n    {\n      "categoryType": "type1",\n      "indexedProperties": [{"name": "field1", "type": "string"}],\n      "metadata": [{"name": "meta1", "type": "string"}]\n    }\n  ]\n}'
              />
              {jsonError && (
                <p className="mt-2 text-sm text-red-500">{jsonError}</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-4">
              {schemaFields.length > 0 && (
                <button
                  onClick={handleDeleteSchema}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Schema
                </button>
              )}
              <button
                onClick={handleJsonSubmit}
                className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors"
                disabled={isLoading}
              >
                Continue to Fields
              </button>
            </div>
          </div>
        ) : viewMode === 'fields' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                Edit your schema fields below. You can add custom fields at any level.
              </p>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto border dark:border-border-color border-light-border-color rounded-md p-4">
              {schemaFields.map((field, index) => renderField(field, [index]))}
              
              <button
                onClick={() => addCustomField([])}
                className="flex items-center space-x-2 text-sm text-primary hover:text-primary-hover mt-4"
              >
                <Plus className="w-4 h-4" />
                <span>Add root field</span>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleAbort}
                  className="w-full sm:w-auto px-4 py-2 border dark:border-border-color border-light-border-color rounded-md dark:text-text-primary text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors"
                  disabled={isLoading}
                >
                  Abort
                </button>
                <button
                  onClick={handleRevertToDefault}
                  className="w-full sm:w-auto px-4 py-2 border dark:border-border-color border-light-border-color rounded-md dark:text-text-primary text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors"
                  disabled={isLoading}
                >
                  Revert to Default
                </button>
              </div>
              <button
                onClick={handleSaveSchema}
                className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Schema'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                Your saved schema structure (read-only view)
              </p>
              <button
                onClick={handleDownloadSchema}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm border dark:border-border-color border-light-border-color rounded-md dark:text-text-primary text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto border dark:border-border-color border-light-border-color rounded-md p-4 dark:bg-gray-900 bg-gray-50">
              <pre className="text-sm dark:text-text-primary text-light-text-primary font-mono whitespace-pre-wrap">
                {JSON.stringify(savedSchema, null, 2)}
              </pre>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-4">
              <button
                onClick={handleDeleteSchema}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete Schema'}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DataManagementPreferencesPage;
